import React from "react";
import { renderContentProps } from "./types";
import {
  Attachment,
  InboxConstants,
  MessageStatus,
  MessageType,
} from "types/conversationInbox";
import {
  MessageBlock,
  MessageContainer,
  resolveMessageBackground,
} from "../styled";
import {
  MessageReaction,
  ReactionsChangeData,
} from "../../components/message-reactions";
import { CachedMicrolink } from "components/common/CachedMicrolink";
import { UnsupportedMessageContent } from "../../components/multi-media";
import {
  MessageMoreOptions,
  GMBMessageMoreOptions,
} from "../../components/message-more-options";
import { urlRegex } from "../../components/post-caption-handler";
import InboxPreferences from "@inbox/pages/conversation-page/inbox-preferences";
import { ReactionsContext } from "../types";
import { renderAccountNameBold } from "./rich-text-message";
import { platformAccountNameMap } from "shared/types/accounts";
import { cn } from "@/lib/utils";

const EMPTY_ATTACHMENTS: Attachment[] = [];

export const allowReactionsMessageTypes = [
  MessageType.igMessage,
  MessageType.ttThreadedComment,
];

export const isUnsupportedAttachment = (
  message: renderContentProps["message"],
  attachments: Attachment[],
  originalMessage: renderContentProps["originalMessage"] | undefined
): boolean => {
  return (
    (attachments || []).length === 0 &&
    typeof message === "string" &&
    message.length <= 0 &&
    !originalMessage?.attachment
  );
};

export const NormalMessage = ({
  conversationId,
  type,
  message,
  isLeft,
  isTeamMessage,
  isRepliedMessage,
  attachments = EMPTY_ATTACHMENTS,
  messageId,
  reactions,
  originalMessage,
  afterPlaceholder,
  onReactionsChange,
  status,
  accountContext,
}: NormalMessageProps): JSX.Element | null => {
  if (originalMessage?.deleted) {
    message = "Message has been deleted.";
  }

  if (message === undefined) {
    return null;
  }

  if (
    ((attachments || []).length > 0 || originalMessage?.attachment) &&
    !message
  ) {
    message = "Shared an attachment";
  }

  const reactionsJsx = InboxPreferences.shouldEnableReaction(
    type,
    reactions,
    onReactionsChange
  ) && (
    <MessageReaction
      isLeft={!!isLeft}
      messageId={messageId}
      reactions={reactions as ReactionsContext}
      onReactionsChange={
        onReactionsChange as (data: ReactionsChangeData) => void
      }
    />
  );

  let link = null;

  const containsUrl = typeof message === "string" && urlRegex.test(message);
  if (containsUrl) {
    const regexMatchArray = (message as string).match(urlRegex) || [];
    link = regexMatchArray[0];
    const [textBefore, textAfter] = (message as string).split(link || "");

    message = (
      <>
        {textBefore}
        <a href={link} target="_blank" rel="noreferrer">
          {link}
        </a>
        {textAfter}
      </>
    );
  } else if (
    accountContext &&
    typeof message === "string" &&
    [
      MessageType.fbMentionV2,
      MessageType.fbMentionInPostV2,
      MessageType.igMentionV2,
      MessageType.igMentionInPostV2,
    ].includes(type)
  ) {
    message = renderAccountNameBold(message, accountContext.login);
  }

  const messageBackgroundStyle = resolveMessageBackground({
    isRepliedMessage,
    isTeamMessage,
    isFailed: status === MessageStatus.failed,
    left: isLeft,
  });

  const messageOptions = resolveMessageOptions({
    type,
    originalMessage,
    isTeamMessage,
    conversationId,
    isLeft,
    accountContext,
  });

  const { optionsBefore, optionsAfter } = resolveMessageOptionsPosition(
    type,
    messageOptions
  );

  if (isUnsupportedAttachment(message, attachments, originalMessage)) {
    message = <UnsupportedMessageContent message={message} />;
  }

  return (
    <MessageContainer left={isLeft}>
      {!isLeft && reactionsJsx}
      {optionsBefore}
      <div
        className={cn(
          "flex flex-col",
          isLeft ? "items-start" : "items-end",
          "[&_a]:!mx-3 [&_a]:!mt-3 [&_a]:!mb-0"
        )}
      >
        <MessageBlock
          style={{ background: messageBackgroundStyle }}
          left={isLeft}
          isTeamMessage={isTeamMessage}
          isRepliedMessage={isRepliedMessage}
          italic={!!originalMessage?.deleted}
        >
          {message}
        </MessageBlock>
        {containsUrl && link && (
          <div className="max-w-full w-[500px] mx-3 mt-3">
            <CachedMicrolink
              url={link}
              className="m-0 w-full"
              apiKey={InboxConstants.microlinkApiKey}
            />
          </div>
        )}
      </div>
      {isLeft && reactionsJsx}
      {optionsAfter}
      {afterPlaceholder}
    </MessageContainer>
  );
};

function resolveMessageOptionsPosition(
  messageType: MessageType,
  element: React.ReactNode
) {
  if (
    messageType === MessageType.gmbReviewReply ||
    messageType === MessageType.fbComment
  ) {
    return { optionsBefore: element, optionsAfter: null };
  }
  return { optionsBefore: null, optionsAfter: element };
}

function resolveMessageOptions({
  type,
  originalMessage,
  isTeamMessage,
  conversationId,
  isLeft,
  accountContext,
}: Pick<
  NormalMessageProps,
  | "type"
  | "originalMessage"
  | "isTeamMessage"
  | "conversationId"
  | "isLeft"
  | "accountContext"
>) {
  if (
    !originalMessage ||
    !InboxPreferences.shouldRenderMessageOptions(originalMessage, isTeamMessage)
  ) {
    return <></>;
  }

  if (type === MessageType.gmbReviewReply) {
    return (
      <GMBMessageMoreOptions
        conversationId={conversationId}
        message={originalMessage}
        position="left"
        label="review"
      />
    );
  }
  const platformName =
    platformAccountNameMap[accountContext?.platformType || "IG"];
  const isTtComment = type === MessageType.ttThreadedComment;
  if (isTtComment) {
    const supportDeleteAction = originalMessage.sender?.platform === "SK";
    return (
      <MessageMoreOptions
        conversationId={conversationId}
        messageId={originalMessage?.id}
        messageHidden={!!originalMessage?.hidden}
        isHiddenProcessing={!!originalMessage?.isHiddenProcessing}
        supportHiddenAction={!!isLeft}
        supportDeleteAction={supportDeleteAction}
        isSending={originalMessage?.status === MessageStatus.pending}
        platform={platformName}
      />
    );
  }

  const isFbComment =
    type === MessageType.fbComment || type === MessageType.fbThreadedComment;

  if (type === MessageType.igThreadedComment) {
    return (
      <MessageMoreOptions
        conversationId={conversationId}
        messageId={originalMessage?.id}
        messageHidden={!!originalMessage?.hidden}
        isHiddenProcessing={!!originalMessage?.isHiddenProcessing}
        supportHiddenAction={!!isLeft}
        isSending={originalMessage?.status === MessageStatus.pending}
        platform={platformName}
      />
    );
  }
  if (isFbComment) {
    return (
      <MessageMoreOptions
        conversationId={conversationId}
        messageId={originalMessage?.id}
        messageHidden={!!originalMessage?.hidden}
        isHiddenProcessing={!!originalMessage?.isHiddenProcessing}
        supportHiddenAction={type === MessageType.fbThreadedComment}
        isSending={originalMessage?.status === MessageStatus.pending}
        platform={platformName}
      />
    );
  }

  return <></>;
}

export type NormalMessageProps = Pick<
  renderContentProps,
  | "type"
  | "message"
  | "isLeft"
  | "isTeamMessage"
  | "isRepliedMessage"
  | "attachments"
  | "messageId"
  | "reactions"
  | "onReactionsChange"
  | "originalMessage"
  | "conversationId"
  | "selected"
  | "onSelected"
  | "status"
  | "accountContext"
> & { afterPlaceholder?: JSX.Element | false };
